<script lang="ts">
	import '../app.css';

	import { onMount, untrack } from 'svelte';
	import { afterNavigate, goto } from '$app/navigation';
	import { page } from '$app/state';
	import { MediaQuery } from 'svelte/reactivity';
	import { geodataStore } from '#lib/stores/geodataStore.svelte.js';
	import { apiKeyStore, API_KEY_LENGTH } from '#lib/stores/apiKeyStore.svelte.js';
	import { SUPPORT_EMAIL } from '#lib/apps/contact.js';
	import { operationalLayersStore } from '#lib/stores/operationalLayersStore.svelte.js';
	import { gisServicesLayersStore } from '#lib/stores/gisServicesLayersStore.svelte.js';
	import { versionsStore } from '#lib/stores/versionsStore.svelte.js';
	import { FetchStatus } from '#lib/types.js';
	import { analyticsStore } from '#lib/stores/analyticsStore.js';
	import {
		platformAppVersion,
		platformBuild,
		platformBuildSha,
		environment,
		geoServer,
		haloFallbackLink,
		gaTrackingId
	} from '#lib/config.js';
	import { getLogger } from '@logtape/logtape';
	import '#lib/logger.js';
	import Header from '#lib/components/Header.svelte';
	import ApiKeySet from '#lib/components/ApiKeySet.svelte';
	import ApiKeyView from '#lib/components/ApiKeyView.svelte';
	import Modal from '#lib/components/Modal.svelte';
	import AbleyButton from '#lib/components/AbleyButton.svelte';
	import CoordinatesFooter from '#lib/components/CoordinatesFooter.svelte';
	import Logo from '#lib/components/Logo.svelte';
	import LogoDefault from '#lib/assets/logos/abley.svg?raw';
	import { getProductSuite } from '#lib/apps/availability.js';
	import { getAppsWithAvailability } from '#lib/apps/index.js';
	import { PRODUCT_SUITE_APPS, PLATFORM_TOOLS } from '#lib/apps/types.js';
	import type { AppId } from '#lib/apps/types.js';
	import { about, platformHelp } from '#lib/apps/sharedHeader.js';
	import { appStore } from '#lib/stores/appStore.svelte.js';
	import type { HeaderItem } from '#lib/components/Header.svelte';
	import type { MenuItem } from '#lib/components/MenuButton.svelte';
	// used for preconnect, so skip `config.ts`
	import { PUBLIC_API_HOST } from '$app/env/public';

	const log = getLogger(['platform', 'layout']);
	log.info('Abley spatial platform: {build}', {
		build: platformBuild,
		version: platformAppVersion,
		sha: platformBuildSha
	});

	const { children } = $props();

	// Before any child effect logs an event.
	analyticsStore.initializeGA(gaTrackingId);

	type ServerErrorState = 'OK' | 'CONNECTION_ERROR' | 'MAINTENANCE_ERROR';
	let serverError = $state<ServerErrorState>('OK');

	let showViewApiKeyModal = $state(false);
	let inputApiKey = $state('');

	const apiKeyValid = $derived(inputApiKey.trim().length === API_KEY_LENGTH);
	const isSaving = $derived(apiKeyStore.apiState.status === FetchStatus.LOADING);

	const saveApiKey = () => {
		if (apiKeyValid) void apiKeyStore.validateAndSave(inputApiKey.trim());
	};

	const resetApiKey = () => {
		inputApiKey = '';
		apiKeyStore.clear();
		window.location.reload();
	};

	// `?version=` owns the active app's data version; (product)/+layout reacts to it.
	function selectVersion(version: string) {
		void goto(`?${new URLSearchParams({ version })}`, { reset: false });
	}

	// Typing a new key clears any server error so the user can retry.
	const onApiKeyInput = (v: string) => {
		inputApiKey = v;
		serverError = 'OK';
	};

	// Surface operational-layers fetch failures via the connection-error modal.
	// The store flips to FAILURE on network / api errors after a key is
	// validated; that modal already covers the "couldn't connect" UX.
	$effect(() => {
		if (operationalLayersStore.status === FetchStatus.FAILURE && serverError === 'OK') {
			serverError = 'CONNECTION_ERROR';
		}
	});

	const narrowScreen = new MediaQuery('max-width: 699px');
	// Writable $derived: the user can dismiss it; re-shows when the query toggles.
	let showScreenWidthWarning = $derived(narrowScreen.current);

	onMount(async () => {
		try {
			const result = await geoServer.healthCheckStatus();
			if (result.status >= 400) {
				serverError = result.status === 503 ? 'MAINTENANCE_ERROR' : 'CONNECTION_ERROR';
			}
		} catch {
			serverError = 'CONNECTION_ERROR';
		}

		await apiKeyStore.init();

		// If the health check failed but the stored key was explicitly rejected
		// on startup, unblock the gate so the user can enter a new key.
		if (serverError === 'CONNECTION_ERROR' && apiKeyStore.startupKeyRejected) {
			serverError = 'OK';
		}
	});

	afterNavigate((state) => {
		if (state.shallow) return;

		const params = state.to?.params ?? {};
		const path = (state.to?.route.id ?? '')
			.replace('(product)/', '')
			.replace('[app]', params.app ?? '')
			.replace('[[view=view]]', params.view ?? '');
		analyticsStore.logNavigation(path);
	});

	// untrack() prevents the stores' internal state from being tracked as a dependency.
	$effect(() => {
		const key = apiKeyStore.apiKey;
		if (key) {
			untrack(() => {
				void geodataStore.loadContextLayer(key);
				void geodataStore.loadConfigData(key);
				void versionsStore.load(key);
				void operationalLayersStore.load(key);
				void gisServicesLayersStore.load(key);
			});
		} else {
			untrack(() => {
				operationalLayersStore.clear();
				gisServicesLayersStore.clear();
				versionsStore.clear();
			});
		}
	});

	// Shared header items. About is always present; the active suite's app menu
	// and the "Tools" menu are added once we can enumerate available apps for the api key.
	const sharedItems = $derived.by<HeaderItem[]>(() => {
		const items: HeaderItem[] = [];

		// Header menus list every app in a group, in declared order, marking
		// unavailable apps disabled rather than hiding them.
		const appsById = getAppsWithAvailability(operationalLayersStore.datasets, {
			'gis-services': gisServicesLayersStore.available
		});
		const activeApp = appStore.app;
		const linksFor = (appIds: readonly AppId[]): MenuItem[] =>
			appIds
				.map((id) => appsById.get(id))
				.filter((entry) => entry !== undefined)
				.map(({ config, available }) => {
					const isActive = config.id === activeApp?.id;
					const dataset = config.datasets[0];
					// The active app lists its data versions as a submenu when it has more than one.
					const versionList =
						isActive && available && dataset ? versionsStore.versionsFor(dataset) : [];
					if (versionList.length > 1) {
						const current = versionsStore.version(dataset!);
						return {
							submenu: {
								title: config.name,
								links: versionList.map((v) => ({
									label: `v${v.version}`,
									active: v.version === current?.version,
									onclick: () => selectVersion(v.version)
								}))
							}
						};
					}
					return {
						link: {
							href: config.basePath,
							label: config.name,
							active: isActive,
							disabled: !available
						}
					};
				});

		// The active app's product suite gets its own menu, titled by the suite
		// name. Platform tools belong to no suite, so no suite menu shows while
		// one is active. Single-app suites (e.g. MIRE) get no menu either —
		// there's nothing to switch between.
		const activeSuiteId = activeApp?.id ? getProductSuite(activeApp.id) : undefined;
		const activeSuite = PRODUCT_SUITE_APPS.find((suite) => suite.id === activeSuiteId);
		// Retired apps keep working for anyone holding a link but are not advertised.
		const suiteApps = (activeSuite?.apps ?? []) as readonly AppId[];
		if (activeSuite && suiteApps.length > 1) {
			const suiteLinks = linksFor(suiteApps);
			if (suiteLinks.length > 0) {
				items.push({ title: activeSuite.name, items: suiteLinks });
			}
		}

		// Shared platform tools — listed disabled when unavailable (a prompt to buy
		// them), unless the active app opts out via `header.hideUnavailableTools`.
		if (activeApp) {
			const allToolLinks = linksFor(PLATFORM_TOOLS);
			const toolLinks = activeApp.header.hideUnavailableTools
				? allToolLinks.filter((item) => !item.link?.disabled)
				: allToolLinks;
			if (toolLinks.length > 0) {
				items.push({ title: 'Tools', items: toolLinks });
			}
		}

		items.push(about);

		// Help: the active app's `header.help` entries, then its release notes link (if any) and version; with no app, the platform version.
		const appHelp: MenuItem[] = activeApp
			? (activeApp.header.help ?? [{ button: { label: 'Get support', action: 'contact-support' } }])
			: [];
		const appVersionHelp: MenuItem[] = activeApp
			? [
					...(activeApp.releaseNotesLink
						? [
								{
									link: {
										label: `${activeApp.name} release notes`,
										href: activeApp.releaseNotesLink,
										type: 'link' as const
									}
								}
							]
						: []),
					{ info: { label: `${activeApp.name}: v${activeApp.version}` } }
				]
			: [...platformHelp];
		items.push({
			title: 'Help',
			items: [...appHelp, ...appVersionHelp]
		});

		return items;
	});

	// `appStore.app` outlives a `(product)` route, so per-app chrome is gated on an app route being mounted.
	const mountedApp = $derived(
		!page.error && /^\/(\(product\)|gis-services)/.test(page.route.id ?? '') ? appStore.app : null
	);

	const appLeading = $derived.by<(HeaderItem | undefined)[]>(() => {
		const config = mountedApp;
		if (!config) return [];
		const items: (HeaderItem | undefined)[] = [config.header.dashboard];
		const programs = config.header.programs?.(geodataStore.configData);
		if (programs) items.push(programs);
		if (config.header.method) {
			const built = config.header.method(geodataStore.systemData);
			if (built) items.push(built);
		}
		const playbook = config.header.playbook?.(geodataStore.systemData);
		if (playbook) items.push(playbook);
		if (config.header.menus) items.push(...config.header.menus());
		return items;
	});

	const logo = $derived(mountedApp?.logo ?? LogoDefault);
	const items = $derived([...appLeading, ...sharedItems]);
</script>

<!--
	Loads global stylesheets and acts as the shared shell for every top-level
	app route. App-specific shells (header, API key gate, geodata loading, etc.)
	live in the app layout — currently src/routes/(product)/+layout.svelte.
-->

<svelte:head>
	<link rel="preconnect" href={PUBLIC_API_HOST} />
	<link rel="dns-prefetch" href={PUBLIC_API_HOST} />
	{#if mountedApp}
		{@const { title, description, keywords } = mountedApp.head}
		<title>{title}</title>
		<meta name="description" content={description} />
		<meta name="keywords" content={keywords} />

		<!-- Open Graph / Facebook -->
		<meta property="og:title" content={title} />
		<meta property="og:description" content={description} />

		<!-- Twitter -->
		<meta name="twitter:title" content={title} />
		<meta name="twitter:description" content={description} />
	{/if}
</svelte:head>

{#snippet headerLogo()}
	<Logo svg={logo} />
{/snippet}

{#snippet supportFooter(type: string)}
	<p class="supportHint">
		If the issue keeps occurring please use the
		<!-- eslint-disable-next-line svelte/no-navigation-without-resolve -- external support-portal URL -->
		<a href={haloFallbackLink} target="_blank"
			>help form<span class="screenReaderOnly"> (opens in new tab)</span></a>
		or contact
		<!-- eslint-disable-next-line svelte/no-navigation-without-resolve -- mailto: link -->
		<a href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(type)}`}>{SUPPORT_EMAIL}</a>.
	</p>
{/snippet}

<a class="skipLink" href="#content">Skip to content</a>

<Header logo={headerLogo} {environment} {items} onaccesskey={() => (showViewApiKeyModal = true)} />

<main id="content" tabindex="-1">
	{@render children()}
</main>

<CoordinatesFooter />

<!-- API key entry — shown when no valid key and no connection error -->
<ApiKeySet
	open={!isSaving && !apiKeyStore.apiKey && serverError !== 'CONNECTION_ERROR'}
	valid={apiKeyValid}
	oncontinue={saveApiKey}
	onvalueinput={onApiKeyInput}
	loading={isSaving}
	errorMessage={apiKeyStore.apiState.error ?? ''}
	{environment} />

<!-- API key view/reset, opened from the profile menu -->
<ApiKeyView
	open={showViewApiKeyModal && serverError !== 'CONNECTION_ERROR'}
	apiKey={apiKeyStore.apiKey ?? ''}
	onclose={() => (showViewApiKeyModal = false)}
	clear={resetApiKey} />

<!-- Connection error modal -->
<Modal open={serverError === 'CONNECTION_ERROR'} title="Connection error">
	<p>
		We couldn't connect to the server right now. Please check your internet connection or try again
		later.
	</p>
	{@render supportFooter('Abley Spatial: connection error')}
</Modal>

<!-- Maintenance error modal -->
<Modal open={serverError === 'MAINTENANCE_ERROR'} title="System under maintenance">
	<p>We're down for maintenance right now, please check back soon.</p>
	{@render supportFooter('Abley Spatial: maintenance error')}
</Modal>

<!-- Screen width warning -->
<Modal open={showScreenWidthWarning} title="Warning">
	{#snippet footer()}
		<AbleyButton onclick={() => (showScreenWidthWarning = false)}>Continue anyway</AbleyButton>
	{/snippet}
	<p>
		This application is not yet designed for smaller screens. For a better experience increase the
		size of your browser window or use a desktop device.
	</p>
</Modal>

<style>
	.supportHint {
		margin-top: var(--space-3);
		color: var(--text-muted);
		font-size: var(--font-size-sm);
	}
</style>
